import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/modern/components/ui/dropdown-menu";
import { Button } from "@/modern/components/ui/button-shadcn";
import { useConfirm } from "@omit/react-confirm-dialog";
import { MoreVertical, Eye, Trash2, Download, Pencil } from "lucide-react";
import { FormattedFile } from "../context/EntityFileContext";
import { isImageFileTyped } from "./FileHelpers";
import { getFileExtension } from "@/modern/utils";

interface FileActionsMenuProps {
  file: FormattedFile;
  onFileOpen: (file: FormattedFile) => void;
  onFileDownload: (file: FormattedFile) => void;
  onFileAnnotate?: (file: FormattedFile) => void;
  onFileDelete: (fileId: string) => void;
  shouldShowAdjustPoints?: (file: FormattedFile) => boolean;
  onAdjustPoints?: (file: FormattedFile) => void;
  triggerClassName?: string;
  align?: "start" | "center" | "end";
}

export const FileActionsMenu = ({ file, onFileOpen, onFileDownload, onFileAnnotate, onFileDelete, triggerClassName, align = "end" }: FileActionsMenuProps) => {
  const confirm = useConfirm();
  const isPDF = getFileExtension(file.name) === "pdf";

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          className={triggerClassName}
          onClick={(e) => {
            e.stopPropagation();
          }}
          variant="secondary"
          size="icon-sm"
        >
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-48 z-[--swivu-bryntum-z-index]">
        <DropdownMenuItem
          disabled={(file.canView !== undefined && !file.canView)}
          onClick={(e) => {
            e.stopPropagation();
            onFileOpen(file);
          }}
        >
          <Eye className="mr-2 h-4 w-4" />
          View
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={(file.canView !== undefined && !file.canView)}
          onClick={(e) => {
            e.stopPropagation();
            onFileDownload(file);
          }}
        >
          <Download className="mr-2 h-4 w-4" />
          Download
        </DropdownMenuItem>
        {isImageFileTyped(file) && !isPDF && onFileAnnotate && (
          <DropdownMenuItem
            disabled={file.isCCImage ? false : !file.canEdit}
            onClick={(e) => {
              e.stopPropagation();
              onFileAnnotate(file);
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Annotate
          </DropdownMenuItem>
        )}
        {/*{isImageFileTyped(file) && !isPDF && shouldShowAdjustPoints?.(file) && onAdjustPoints && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onAdjustPoints(file);
            }}
          >
            <Award className="mr-2 h-4 w-4" />
            Adjust Points
          </DropdownMenuItem>
        )}*/}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!file.canEdit}
          onClick={async (e) => {
            e.stopPropagation();
            if (
              file.canEdit &&
              (await confirm({
                title: "Confirm Delete",
                description: `Are you sure you want to delete "${file.name}"?`,
                customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
                  <div className="space-x-4">
                    <Button variant="ghost" onClick={cancel}>
                      Cancel
                    </Button>
                    <Button className="dark:bg-red-900 dark:text-red-200 bg-red-100 text-red-800 hover:dark:bg-red-950 hover:dark:text-red-300 hover:bg-red-200 transition-colors" onClick={confirm}>
                      Delete
                    </Button>
                  </div>
                )
              }))
            ) {
              onFileDelete(file.id.toString());
            }
          }}
          className="text-destructive focus:text-destructive dark:focus:text-destructive-foreground"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
